import { ChangeDetectorRef, Component, ElementRef, inject, OnInit, ViewChild, Output, EventEmitter, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { DepotService } from 'src/app/services/modules/instant-rate-manager/depot.service';
import { AlertUtil } from 'src/app/shared/utils/alert-dialog.util';
import { ZoneService } from 'src/app/services/modules/instant-rate-manager/zone.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

declare var google: any;

interface ZoneOption {
  value: number;
  label: string;
}

interface Depot {
  id: number;
  name: string;
  lat: string;
  lng: string;
  full_address: string;
  zone_details: { id: number; name: string };
}

@Component({
  selector: 'app-manage-depots',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule],
  templateUrl: './manage-depots.component.html',
  styleUrl: './manage-depots.component.scss',
})
export class ManageDepotsComponent implements OnInit {
  @ViewChild('addressInput') set addressInput(ref: ElementRef) {
    if (ref) this.initAutocompleteOnElement(ref.nativeElement, '_autoMainActive');
  }
  @ViewChild('depotModalTemplate') depotModalTemplate!: TemplateRef<any>;

  @Output() noZonesFound = new EventEmitter<void>();

  private zoneService = inject(ZoneService);
  private depotService = inject(DepotService);
  private modalService = inject(NgbModal);
  private cdr = inject(ChangeDetectorRef);

  private modalRef: any;

  /**
   * STATE
   */
  depots: Depot[] = [];
  selectedDepot: Depot | null = null;
  zoneOptions: ZoneOption[] = [];

  isCreatingNew = false;
  isLoading = false;
  isLoadingZones = false;
  hasNoZones = false;
  currentSort = '-updated_at';
  paginationInfo: any = null;
  search = '';
  depotListingSearch$ = new Subject<string>();

  zoneSearchTerm$ = new Subject<string>();

  ngOnInit(): void {
    this.getProviderDepots();
    this.setupZoneSearch();
    this.setupDepotSearch();
    this.loadZones('');
  }

  /**
   * GOOGLE AUTOCOMPLETE
   */
  private initAutocompleteOnElement(el: HTMLElement, flag: string) {
    if ((el as any)[flag]) return;
    (el as any)[flag] = true;

    setTimeout(() => {
      if (typeof google === 'undefined') return;

      const autocomplete = new google.maps.places.Autocomplete(el, {
        types: ['address'],
        componentRestrictions: { country: 'AU' },
        fields: ['formatted_address', 'geometry']
      });

      el.addEventListener('keydown', (e: KeyboardEvent) => {
        const pacContainer = document.querySelector('.pac-container') as HTMLElement;
        if (e.key === 'Enter' && pacContainer && pacContainer.offsetParent) {
          e.preventDefault();
        }
      });

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();

        if (this.selectedDepot && place.formatted_address) {
          this.selectedDepot.full_address = place.formatted_address;
          if (place.geometry && place.geometry.location) {
            this.selectedDepot.lat = place.geometry.location.lat().toFixed(7);
            this.selectedDepot.lng = place.geometry.location.lng().toFixed(7);
          }
          this.cdr.detectChanges();
        }
      });
    }, 200);
  }

  /**
   * DATA FETCHING
   */
  getProviderDepots(page: number = 1) {
    this.cancelEdit();
    this.isLoading = true;

    const payload = {
      ordering: this.currentSort,
      page: page,
      page_size: 5,
      name: this.search,
    };

    this.depotService.getDepots(payload).subscribe({
      next: (res: any) => {
        const response = res.data;
        this.paginationInfo = {
          count: response.count,
          current_page: response.current_page,
          total_pages: response.total_pages,
          next: response.next,
          previous: response.previous
        };
        const apiData = response.result || [];
        this.depots = apiData.map((d: any) => this.mapToInternalModel(d));
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.isLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * ACTIONS
   */
  async saveDepot() {
    const d = this.selectedDepot;

    const hasName = !!d?.name && d.name.trim() !== '';
    const hasZone = !!d?.zone_details?.id && d.zone_details.id !== 0;
    const hasAddress = !!d?.full_address && d.full_address.trim() !== '';
    const hasCoords = !!d?.lat && d.lat.trim() !== '' && !!d?.lng && d.lng.trim() !== '';

    if (!hasName || !hasZone || !hasAddress) {
      AlertUtil.error('Please ensure Depot Name, Zone, and Address are provided.');
      return;
    }

    if (!hasCoords) {
      AlertUtil.error('Please select an address from the dropdown suggestions to ensure location coordinates are captured correctly.');
      return;
    }

    const payload = this.mapToPayload(d!);

    try {
      this.isLoading = true;
      if (this.isCreatingNew) {
        await this.depotService.createDepot(payload);
        this.modalRef?.close();
        AlertUtil.success('Depot created successfully!');
      } else {
        await this.depotService.updateDepot(d!.id, payload);
        AlertUtil.success('Depot updated successfully!');
      }
      this.getProviderDepots(this.paginationInfo?.current_page || 1);
    } catch (err: any) {
      console.error('Depot save error:', err);
      AlertUtil.error(err?.error?.message || err?.message || 'An unexpected error occurred.');
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  async deleteDepot(id: number, event: Event) {
    event.stopPropagation();
    const result = await AlertUtil.confirm('Delete Depot?', 'This action cannot be undone.');

    if (result.isConfirmed) {
      try {
        await this.depotService.deleteDepot(id);
        AlertUtil.success('Depot deleted successfully!');
        this.getProviderDepots(this.paginationInfo?.current_page || 1);
      } catch (err: any) {
        AlertUtil.error('Failed to delete depot.');
      }
    }
  }

  /**
   * UI HELPERS
   */
  selectDepot(depot: Depot) {
    this.isCreatingNew = false;
    this.selectedDepot = JSON.parse(JSON.stringify(depot));

    if (this.selectedDepot?.zone_details?.id) {
      const activeZone = {
        value: this.selectedDepot.zone_details.id,
        label: this.selectedDepot.zone_details.name,
      };
      if (!this.zoneOptions.some((opt) => opt.value === activeZone.value)) {
        this.zoneOptions = [activeZone, ...this.zoneOptions];
      }
    }
    this.cdr.detectChanges();
  }

  initiateNewDepot() {
    this.isCreatingNew = true;
    this.selectedDepot = {
      id: 0,
      name: '',
      lat: '',
      lng: '',
      full_address: '',
      zone_details: { id: 0, name: '' }
    };
    this.loadZones('');
    this.cdr.detectChanges();
    this.modalRef = this.modalService.open(this.depotModalTemplate, { centered: true, size: 'lg' });
    this.modalRef.result.then(() => {}, () => this.cancelEdit());

    // NgbModal renders outside the component view, so @ViewChild won't detect modal inputs.
    // Manually find and initialize autocomplete after the modal is rendered.
    setTimeout(() => {
      const modalWindow = document.querySelector('ngb-modal-window');
      if (modalWindow) {
        const addressInput = modalWindow.querySelector('input[name="newAddress"]') as HTMLElement;
        if (addressInput) this.initAutocompleteOnElement(addressInput, '_autoModalActive');
      }
    }, 300);
  }

  cancelEdit() {
    this.selectedDepot = null;
    this.isCreatingNew = false;
    this.cdr.detectChanges();
  }

  onPageChange(direction: 'next' | 'prev') {
    if (!this.paginationInfo) return;
    const targetPage = direction === 'next' ? this.paginationInfo.current_page + 1 : this.paginationInfo.current_page - 1;
    if (targetPage >= 1 && targetPage <= this.paginationInfo.total_pages) {
      this.getProviderDepots(targetPage);
    }
  }

  onSortChange() {
    this.getProviderDepots(1);
  }

  /**
   * SEARCH LOGIC
   */
  private setupZoneSearch() {
    this.zoneSearchTerm$.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      tap(() => (this.isLoadingZones = true)),
      switchMap((term) => this.fetchZones(term))
    ).subscribe({
      next: (data) => {
        this.zoneOptions = data;
        this.isLoadingZones = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.isLoadingZones = false;
        this.cdr.detectChanges();
      }
    });
  }

  private setupDepotSearch() {
    this.depotListingSearch$.pipe(
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe(() => {
      this.getProviderDepots(1);
    });
  }

  async fetchZones(term: string): Promise<ZoneOption[]> {
    try {
      const response = await this.zoneService.getProviderZones({ name: term, page_size: 50, is_active: true });
      return (response.result || []).map((item: any) => ({
        value: item.id,
        label: item.name
      }));
    } catch (error) {
      return [];
    }
  }

  private async loadZones(term: string = '') {
    this.isLoadingZones = true;
    this.zoneOptions = await this.fetchZones(term);
    this.isLoadingZones = false;

    if (term === '' && this.zoneOptions.length === 0) {
      this.hasNoZones = true;
      await AlertUtil.info('you dont have any zones yet, please go to manage location to set zone');
      this.redirectToZones();
    } else {
      this.hasNoZones = false;
    }

    this.cdr.detectChanges();
  }

  redirectToZones() {
    this.noZonesFound.emit();
  }

  onZoneSelected(selectedItem: any) {
    if (!this.selectedDepot) return;

    const zoneId = selectedItem.value;
    const zone = this.zoneOptions.find(z => z.value === zoneId);

    if (zone) {
      this.selectedDepot.zone_details = { id: zone.value, name: zone.label };
    } else {
      this.selectedDepot.zone_details = { id: 0, name: '' };
    }
    this.cdr.detectChanges();
  }

  /**
   * MAPPING
   */
  private mapToInternalModel(d: any): Depot {
    return {
      id: d.id,
      name: d.name,
      lat: d.latitude?.toString() || '',
      lng: d.longitude?.toString() || '',
      full_address: d.full_address || '',
      zone_details: d.zone_details || { id: 0, name: '' },
    };
  }

  private mapToPayload(depot: Depot) {
    return {
      zone_id: depot.zone_details.id !== 0 ? depot.zone_details.id : null,
      name: depot.name,
      latitude: (depot.lat && depot.lat.trim() !== '') ? parseFloat(depot.lat) : null,
      longitude: (depot.lng && depot.lng.trim() !== '') ? parseFloat(depot.lng) : null,
      full_address: depot.full_address,
    };
  }
}
